import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@mui/material';

export const gainPercentage = (base, other) => {
    const b = Number(base);
    const o = Number(other);
    if (!isFinite(b) || !isFinite(o) || b <= 0) return null;
    return ((b - o) / b) * 100;
};

export const formatPercentage = (value) =>
    value === null ? '-' : value.toFixed(2) + '%';

const WeightReportTable = ({ data, selectedMonth, isPdf = false }) => {
    const getDatesInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const lastDate = new Date(year, month + 1, 0).getDate();
        const dates = [];

        for (let day = 1; day <= lastDate; day++) {
            dates.push(new Date(year, month, day));
        }

        return dates;
    };

    const date = new Date(selectedMonth);
    const datesInMonth = getDatesInMonth(date);

    const dataMap = data.reduce((acc, item) => {
        if (!acc[item.collectionPointID]) {
            acc[item.collectionPointID] = {
                collectionPointName: item.collectionPointName,
                data: {},
                totalFieldWeight: 0,
                totalFactoryWeight: 0,
                totalCheckrollWeight: 0,
                totalFIFA: 0,
                totalFACR: 0
            };
        }

        item.data.forEach(element => {
            const date = new Date(element.createdDate).toLocaleDateString("en-GB");

            if (!acc[item.collectionPointID].data[date]) {
                acc[item.collectionPointID].data[date] = {
                    fieldWeight: 0,
                    factoryWeight: 0,
                    checkrollWeight: 0
                };
            }

            acc[item.collectionPointID].data[date].fieldWeight += element.fieldWeight || 0;
            acc[item.collectionPointID].data[date].factoryWeight += element.factoryWeight || 0;
            acc[item.collectionPointID].data[date].checkrollWeight += element.checkrollWeight || 0;
            acc[item.collectionPointID].data[date].totalFIFA += (element.fieldWeight - element.factoryWeight) || 0;
            acc[item.collectionPointID].data[date].totalFACR += (element.factoryWeight - element.checkrollWeight) || 0;

            acc[item.collectionPointID].totalFieldWeight += element.fieldWeight || 0;
            acc[item.collectionPointID].totalFactoryWeight += element.factoryWeight || 0;
            acc[item.collectionPointID].totalCheckrollWeight += element.checkrollWeight || 0;
            acc[item.collectionPointID].totalFIFA += (element.fieldWeight - element.factoryWeight) || 0;
            acc[item.collectionPointID].totalFACR += (element.factoryWeight - element.checkrollWeight) || 0;
        });

        return acc;
    }, {});

    const collectionPoints = Object.keys(dataMap);

    const pdfLine = '1px solid #757575 !important';
    const pdfTableSx = isPdf
        ? {
            borderCollapse: 'separate',
            borderSpacing: 0,
            border: 'none',
            borderTop: 'none !important',
            borderLeft: pdfLine,
            '& .MuiTableCell-root': {
                border: 'none',
                borderRight: pdfLine,
                borderBottom: pdfLine,
            },
            '& thead tr:first-of-type .MuiTableCell-root': {
                borderTop: pdfLine,
            },
        }
        : {};

    return (
        <TableContainer component={Paper} sx={{ height: isPdf ? '100%' : '55vh', width: isPdf ? '100%' : '93vw' }}>
            {data.length ? (
                <Table sx={pdfTableSx}>
                    <TableHead sx={{ position: isPdf ? 'static' : 'sticky', background: 'white' }}>
                        <TableRow>
                            <TableCell rowSpan={3}><strong>Date</strong></TableCell>
                            {collectionPoints.map((cp) => (
                                <React.Fragment key={cp}>
                                    <TableCell colSpan={7} align="center"><strong>{dataMap[cp].collectionPointName}</strong></TableCell>
                                </React.Fragment>
                            ))}
                        </TableRow>
                        <TableRow>
                            {collectionPoints.map((cp) => (
                                <React.Fragment key={cp}>
                                    <TableCell rowSpan={2} align="center"><strong>Field Weight</strong></TableCell>
                                    <TableCell rowSpan={2} align="center"><strong>Factory Weight</strong></TableCell>
                                    <TableCell rowSpan={2} align="center"><strong>Checkroll Weight</strong></TableCell>
                                    <TableCell colSpan={4} align="center"><strong>Difference</strong></TableCell>
                                </React.Fragment>
                            ))}
                        </TableRow>
                        <TableRow>
                            {collectionPoints.map((cp) => (
                                <React.Fragment key={cp}>
                                    <TableCell align="center"><strong>FI/FA</strong></TableCell>
                                    <TableCell align="center"><strong>FI/FA %</strong></TableCell>
                                    <TableCell align="center"><strong>FA/CR</strong></TableCell>
                                    <TableCell align="center"><strong>FA/CR %</strong></TableCell>
                                </React.Fragment>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {datesInMonth.map((date) => {
                            const dateString = date.toLocaleDateString("en-GB");
                            const shouldDisplayRow = collectionPoints.some((cp) => {
                                const rowData = dataMap[cp].data[dateString] || {
                                    fieldWeight: 0,
                                    factoryWeight: 0,
                                    checkrollWeight: 0
                                };
                                return rowData.fieldWeight !== 0 || rowData.factoryWeight !== 0 || rowData.checkrollWeight !== 0;
                            });

                            if (!shouldDisplayRow) {
                                return null;
                            }

                            return (
                                <TableRow key={dateString}>
                                    <TableCell>{dateString}</TableCell>
                                    {collectionPoints.map((cp) => {
                                        const rowData = dataMap[cp].data[dateString] || {
                                            fieldWeight: 0,
                                            factoryWeight: 0,
                                            checkrollWeight: 0
                                        };
                                        const formatValue = (value) => (value <= 0 ? '-' : value);
                                        return (
                                            <React.Fragment key={cp}>
                                                <TableCell style={{ textAlign: 'center' }}>{formatValue(rowData.fieldWeight)}</TableCell>
                                                <TableCell style={{ textAlign: 'center' }}>{formatValue(rowData.factoryWeight)}</TableCell>
                                                <TableCell style={{ textAlign: 'center' }}>{formatValue(rowData.checkrollWeight)}</TableCell>
                                                <TableCell style={{ textAlign: 'center' }}>
                                                    {(rowData.fieldWeight - rowData.factoryWeight)}
                                                </TableCell>
                                                <TableCell style={{ textAlign: 'center' }}>
                                                    {formatPercentage(gainPercentage(rowData.fieldWeight, rowData.factoryWeight))}
                                                </TableCell>
                                                <TableCell style={{ textAlign: 'center' }}>
                                                    {(rowData.factoryWeight - rowData.checkrollWeight)}
                                                </TableCell>
                                                <TableCell style={{ textAlign: 'center' }}>
                                                    {formatPercentage(gainPercentage(rowData.factoryWeight, rowData.checkrollWeight))}
                                                </TableCell>
                                            </React.Fragment>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}


                        <TableRow>
                            <TableCell><strong>Total</strong></TableCell>
                            {collectionPoints.map((cp) => {
                                const formatValue = (value) => (value <= 0 ? '-' : value);

                                return (
                                    <React.Fragment key={cp}>
                                        <TableCell style={{ textAlign: 'center' }}>
                                            <strong>{formatValue(dataMap[cp].totalFieldWeight)}</strong>
                                        </TableCell>
                                        <TableCell style={{ textAlign: 'center' }}>
                                            <strong>{formatValue(dataMap[cp].totalFactoryWeight)}</strong>
                                        </TableCell>
                                        <TableCell style={{ textAlign: 'center' }}>
                                            <strong>{formatValue(dataMap[cp].totalCheckrollWeight)}</strong>
                                        </TableCell>
                                        <TableCell style={{ textAlign: 'center' }}>
                                            <strong>{formatValue(dataMap[cp].totalFIFA)}</strong>
                                        </TableCell>
                                        <TableCell style={{ textAlign: 'center' }}>
                                            <strong>{formatPercentage(gainPercentage(dataMap[cp].totalFieldWeight, dataMap[cp].totalFactoryWeight))}</strong>
                                        </TableCell>
                                        <TableCell style={{ textAlign: 'center' }}>
                                            <strong>{formatValue(dataMap[cp].totalFACR)}</strong>
                                        </TableCell>
                                        <TableCell style={{ textAlign: 'center' }}>
                                            <strong>{formatPercentage(gainPercentage(dataMap[cp].totalFactoryWeight, dataMap[cp].totalCheckrollWeight))}</strong>
                                        </TableCell>
                                    </React.Fragment>
                                );
                            })}
                        </TableRow>


                    </TableBody>
                </Table>
            ) : (<p></p>)}
        </TableContainer>
    );
};

export default WeightReportTable;